import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Plus, Trash2, Edit, X, AlertTriangle, Download, ArrowDownToLine, ArrowUpFromLine, Settings2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIAS = ['Medicamentos', 'Alimentação', 'Material clínico', 'Outros'];
const COR_CAT = {
  'Medicamentos': 'bg-red-100 text-red-700 border-red-200',
  'Alimentação': 'bg-amber-100 text-amber-700 border-amber-200',
  'Material clínico': 'bg-blue-100 text-blue-700 border-blue-200',
  'Outros': 'bg-gray-100 text-gray-700 border-gray-200',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const p = iso.slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
};

function ProdutoForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({
    nome: initial?.nome || '',
    concentracao: initial?.concentracao || '',
    validade: initial?.validade || '',
    stock_atual: initial?.stock_atual ?? 0,
    stock_minimo: initial?.stock_minimo ?? 0,
    categoria: initial?.categoria || 'Medicamentos',
    notas: initial?.notas || '',
  });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.nome.trim()) { alert('Nome obrigatório'); return; }
    try { await onSave(f); } catch (err) { alert(err.response?.data?.detail || 'Erro a guardar'); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="text-xs font-medium">Nome do produto (princípio ativo se for medicamento) *</label>
        <input data-testid="stock-form-nome" value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} required
          className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium">Concentração</label>
          <input data-testid="stock-form-concentracao" value={f.concentracao} placeholder="ex: 500 mg/ml" onChange={e => setF({ ...f, concentracao: e.target.value })}
            className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium">Validade</label>
          <input type="date" value={f.validade || ''} onChange={e => setF({ ...f, validade: e.target.value })}
            className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium">Stock atual *</label>
          <input type="number" min="0" required value={f.stock_atual} onChange={e => setF({ ...f, stock_atual: parseInt(e.target.value) || 0 })}
            className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium">Stock mínimo *</label>
          <input type="number" min="0" required value={f.stock_minimo} onChange={e => setF({ ...f, stock_minimo: parseInt(e.target.value) || 0 })}
            className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium">Categoria *</label>
          <select value={f.categoria} onChange={e => setF({ ...f, categoria: e.target.value })}
            className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm">
            {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium">Notas (opcional)</label>
        <textarea value={f.notas} onChange={e => setF({ ...f, notas: e.target.value })} rows={2}
          className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
        <button type="submit" data-testid="stock-form-submit" className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium">Guardar</button>
      </div>
    </form>
  );
}

function MovimentoForm({ produto, onClose, onDone }) {
  const [tipo, setTipo] = useState('saida');
  const [quantidade, setQuantidade] = useState(1);
  const [motivo, setMotivo] = useState('');
  const [local, setLocal] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (quantidade <= 0) { alert('Quantidade deve ser positiva'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/stock/produtos/${produto.id}/movimentos`, { tipo, quantidade, motivo, local });
      onDone?.();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro a registar movimento');
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <form onSubmit={submit} className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-md m-4 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Movimento — {produto.nome}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-muted-foreground">Stock atual: <b className={produto.stock_atual <= produto.stock_minimo ? 'text-red-600' : 'text-foreground'}>{produto.stock_atual}</b> · Mínimo: {produto.stock_minimo}</p>
        <div>
          <label className="text-xs font-medium">Tipo</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <button type="button" onClick={() => setTipo('entrada')} data-testid="mov-tipo-entrada"
              className={`px-2 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 ${tipo === 'entrada' ? 'bg-green-600 text-white' : 'bg-muted'}`}>
              <ArrowDownToLine className="w-3.5 h-3.5" /> Entrada
            </button>
            <button type="button" onClick={() => setTipo('saida')} data-testid="mov-tipo-saida"
              className={`px-2 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 ${tipo === 'saida' ? 'bg-red-600 text-white' : 'bg-muted'}`}>
              <ArrowUpFromLine className="w-3.5 h-3.5" /> Saída
            </button>
            <button type="button" onClick={() => setTipo('ajuste')} data-testid="mov-tipo-ajuste"
              className={`px-2 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 ${tipo === 'ajuste' ? 'bg-amber-600 text-white' : 'bg-muted'}`}>
              <Settings2 className="w-3.5 h-3.5" /> Ajuste
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium">{tipo === 'ajuste' ? 'Novo stock total' : 'Quantidade'}</label>
          <input type="number" min="0" value={quantidade} onChange={e => setQuantidade(parseInt(e.target.value) || 0)} required
            data-testid="mov-quantidade"
            className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium">{tipo === 'saida' ? 'Local / motivo da saída' : 'Motivo'}</label>
          <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder={tipo === 'saida' ? 'Ex: tirei 3 ketaminas para anestesia' : 'Ex: encomenda recebida'}
            data-testid="mov-motivo"
            className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </div>
        {tipo === 'saida' && (
          <div>
            <label className="text-xs font-medium">Para onde / a quem (opcional)</label>
            <input value={local} onChange={e => setLocal(e.target.value)} placeholder="Ex: Bloco operatório, Box A, etc."
              data-testid="mov-local"
              className="w-full mt-1 rounded-xl border border-border bg-background px-3 py-2 text-sm" />
          </div>
        )}
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
          <button type="submit" disabled={loading} data-testid="mov-submit"
            className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {loading ? 'A guardar…' : 'Registar'}
          </button>
        </div>
      </form>
    </div>
  );
}

function HistoricoModal({ produto, onClose }) {
  const [movs, setMovs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/stock/produtos/${produto.id}/movimentos`).then(r => setMovs(r.data)).catch(() => {});
  }, [produto.id]);
  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/stock/produtos/${produto.id}/historico/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Stock_${produto.nome.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert(err.response?.data?.detail || 'Erro ao gerar PDF'); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-3xl m-4 max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-heading font-bold text-lg">Histórico — {produto.nome}</h3>
          <div className="flex gap-2">
            <button onClick={downloadPdf} data-testid="historico-pdf"
              className="flex items-center gap-1 bg-red-600 text-white rounded-full px-3 py-1.5 text-xs">
              <Download className="w-3.5 h-3.5" /> Exportar PDF
            </button>
            <button onClick={onClose}><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto">
          {movs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Sem movimentos registados.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-left py-2">Data</th>
                  <th className="text-left py-2">Tipo</th>
                  <th className="text-center py-2">Qtd</th>
                  <th className="text-left py-2">Motivo / Local</th>
                  <th className="text-left py-2">Utilizador</th>
                </tr>
              </thead>
              <tbody>
                {movs.map(m => (
                  <tr key={m.id} className="border-b border-border/30">
                    <td className="py-2 text-xs">{fmtDateTime(m.timestamp)}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${m.tipo === 'entrada' ? 'bg-green-100 text-green-700' : m.tipo === 'saida' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {m.tipo === 'entrada' ? '+ Entrada' : m.tipo === 'saida' ? '− Saída' : '= Ajuste'}
                      </span>
                    </td>
                    <td className="py-2 text-center font-bold">{m.quantidade}</td>
                    <td className="py-2 text-xs">
                      {m.motivo}
                      {m.local && <span className="text-muted-foreground"> · {m.local}</span>}
                    </td>
                    <td className="py-2 text-xs text-muted-foreground">{m.utilizador}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StockPage() {
  const [produtos, setProdutos] = useState([]);
  const [alertas, setAlertas] = useState({ rotura: [], a_expirar: [], total_alertas: 0 });
  const [filtro, setFiltro] = useState('');
  const [cat, setCat] = useState('');
  const [pagina, setPagina] = useState(0);
  const PER_PAGE = 20;
  const [showForm, setShowForm] = useState(false);
  const [editProd, setEditProd] = useState(null);
  const [movProd, setMovProd] = useState(null);
  const [histProd, setHistProd] = useState(null);
  const [showAlertas, setShowAlertas] = useState(false);

  const carregar = () => {
    axios.get(`${API}/stock/produtos`).then(r => setProdutos(r.data)).catch(() => {});
    axios.get(`${API}/stock/alertas`).then(r => setAlertas(r.data)).catch(() => {});
  };
  useEffect(() => { carregar(); }, []);

  const filtrados = produtos.filter(p => {
    if (cat && p.categoria !== cat) return false;
    if (filtro && !p.nome.toLowerCase().includes(filtro.toLowerCase())) return false;
    return true;
  });

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / PER_PAGE));
  const paginaAtual = Math.min(pagina, totalPaginas - 1);
  const visiveis = filtrados.slice(paginaAtual * PER_PAGE, (paginaAtual + 1) * PER_PAGE);

  const apagar = async (p) => {
    if (!window.confirm(`Apagar produto "${p.nome}" e todo o seu histórico?`)) return;
    try { await axios.delete(`${API}/stock/produtos/${p.id}`); carregar(); }
    catch (err) { alert(err.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-foreground flex items-center gap-2">
            <Package className="w-7 h-7 text-primary" /> Stock
          </h1>
          <p className="text-sm text-muted-foreground">Gestão interna de medicamentos, alimentação e material clínico</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {alertas.total_alertas > 0 && (
            <button onClick={() => setShowAlertas(true)} data-testid="stock-alertas-btn"
              className="flex items-center gap-1 bg-red-600 text-white rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
              <AlertTriangle className="w-4 h-4" /> {alertas.total_alertas} Alerta{alertas.total_alertas > 1 ? 's' : ''}
            </button>
          )}
          <button onClick={async () => {
            try {
              const r = await axios.get(`${API}/stock/relatorio/pdf`, { responseType: 'blob' });
              const blob = new Blob([r.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
              a.href = url; a.download = `AnimaLog_StockGeral_${stamp}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
            } catch (err) { alert(err.response?.data?.detail || 'Erro ao gerar PDF geral'); }
          }} data-testid="stock-pdf-geral"
            className="flex items-center gap-1 bg-red-600/90 text-white rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
            <Download className="w-4 h-4" /> PDF Geral
          </button>
          <button onClick={() => { setEditProd(null); setShowForm(true); }} data-testid="stock-novo-btn"
            className="flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110">
            <Plus className="w-4 h-4" /> Novo Produto
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <input data-testid="stock-filtro"
          placeholder="Procurar por nome…" value={filtro} onChange={e => { setFiltro(e.target.value); setPagina(0); }}
          className="flex-1 min-w-[200px] rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        <select value={cat} onChange={e => { setCat(e.target.value); setPagina(0); }} data-testid="stock-cat-filter"
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
          <option value="">Todas as categorias</option>
          {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-muted-foreground bg-muted/30">
            <tr>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Categoria</th>
              <th className="text-left p-3">Concentração</th>
              <th className="text-center p-3">Stock</th>
              <th className="text-left p-3">Validade</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-muted-foreground py-8">Sem produtos registados.</td></tr>
            ) : visiveis.map(p => {
              const emRotura = (p.stock_atual ?? 0) <= (p.stock_minimo ?? 0);
              return (
                <tr key={p.id} data-testid={`stock-row-${p.id}`} className={`border-t border-border ${emRotura ? 'bg-red-50/40' : ''}`}>
                  <td className="p-3 font-medium">{p.nome}{p.notas && <div className="text-xs text-muted-foreground">{p.notas}</div>}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${COR_CAT[p.categoria] || COR_CAT.Outros}`}>{p.categoria}</span></td>
                  <td className="p-3 text-xs">{p.concentracao || '—'}</td>
                  <td className="p-3 text-center">
                    <span className={`font-bold ${emRotura ? 'text-red-600' : ''}`}>{p.stock_atual ?? 0}</span>
                    <span className="text-xs text-muted-foreground"> / min {p.stock_minimo ?? 0}</span>
                    {emRotura && <AlertTriangle className="w-3.5 h-3.5 text-red-600 inline-block ml-1" />}
                  </td>
                  <td className="p-3 text-xs">{fmtDate(p.validade)}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setMovProd(p)} title="Movimento" data-testid={`stock-mov-${p.id}`}
                        className="p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary">
                        <ArrowUpFromLine className="w-4 h-4" />
                      </button>
                      <button onClick={() => setHistProd(p)} title="Histórico"
                        className="p-1.5 rounded-full bg-muted hover:bg-muted/80">
                        <Package className="w-4 h-4" />
                      </button>
                      <button onClick={() => { setEditProd(p); setShowForm(true); }} title="Editar"
                        className="p-1.5 rounded-full bg-muted hover:bg-muted/80">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => apagar(p)} title="Apagar"
                        className="p-1.5 rounded-full text-destructive hover:bg-destructive/10">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPaginas > 1 && (
        <div className="flex justify-center items-center gap-2 mt-2" data-testid="stock-paginacao">
          <button onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={paginaAtual === 0} className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30 text-sm">←</button>
          <span className="text-sm text-muted-foreground">Página {paginaAtual + 1} de {totalPaginas} · {filtrados.length} produtos</span>
          <button onClick={() => setPagina(p => Math.min(totalPaginas - 1, p + 1))} disabled={paginaAtual >= totalPaginas - 1} className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30 text-sm">→</button>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-lg m-4 p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-lg">{editProd ? 'Editar' : 'Novo'} Produto</h3>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <ProdutoForm initial={editProd} onCancel={() => setShowForm(false)}
              onSave={async (f) => {
                if (editProd) await axios.put(`${API}/stock/produtos/${editProd.id}`, f);
                else await axios.post(`${API}/stock/produtos`, f);
                setShowForm(false); carregar();
              }} />
          </div>
        </div>
      )}

      {movProd && <MovimentoForm produto={movProd} onClose={() => setMovProd(null)} onDone={carregar} />}
      {histProd && <HistoricoModal produto={histProd} onClose={() => setHistProd(null)} />}

      {showAlertas && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-xl m-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><AlertTriangle className="text-red-600 w-5 h-5" /> Alertas de Stock</h3>
              <button onClick={() => setShowAlertas(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              {alertas.rotura.length > 0 && (
                <div>
                  <h4 className="font-bold text-sm mb-2 text-red-600">Em rotura / abaixo do mínimo ({alertas.rotura.length})</h4>
                  <ul className="text-sm space-y-1">
                    {alertas.rotura.map(p => (
                      <li key={p.id} className="flex justify-between bg-red-50 px-2 py-1 rounded">
                        <span>{p.nome}</span>
                        <span className="text-red-700 font-bold">{p.stock_atual} / min {p.stock_minimo}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {alertas.a_expirar.length > 0 && (
                <div>
                  <h4 className="font-bold text-sm mb-2 text-amber-600">A expirar em ≤ 30 dias ({alertas.a_expirar.length})</h4>
                  <ul className="text-sm space-y-1">
                    {alertas.a_expirar.map(p => (
                      <li key={p.id} className="flex justify-between bg-amber-50 px-2 py-1 rounded">
                        <span>{p.nome}</span>
                        <span className="text-amber-700">{fmtDate(p.validade)} ({p.dias_para_validade}d)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {alertas.total_alertas === 0 && <p className="text-sm text-muted-foreground">Sem alertas. ✅</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
